import { motion } from "framer-motion";
import { ArrowRight, Heart, Scissors, Play } from "lucide-react";
import { Link } from "react-router";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import Blob from "@/components/ui/Blob";
import StitchDots from "@/components/ui/StitchDots";

export default function HeroVariantA() {
  const theme = usePalette();
  return (
    <section
      className="relative min-h-dvh flex items-center overflow-hidden pt-20"
      style={{ backgroundColor: colors.cream }}
    >
      <Blob className="w-96 h-96 top-10 -left-20" color={theme.blob1} />
      <Blob className="w-80 h-80 bottom-10 right-0" color={theme.blob2} />
      <Blob className="w-64 h-64 top-1/2 left-1/3" color={theme.blob3} />
      <StitchDots />

      <motion.div
        className="absolute z-20 rounded-2xl shadow-lg px-4 py-3 flex items-center gap-2 hidden lg:flex"
        style={{ backgroundColor: colors.cream, top: "22%", left: "10%" }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: [0, -6, 0] }}
        transition={{
          opacity: { duration: 0.6, delay: 1.2 },
          y: { repeat: Infinity, duration: 3, ease: "easeInOut", delay: 1.2 },
        }}
      >
        <Heart
          size={18}
          style={{ color: colors.berryPink }}
          fill={colors.berryPink}
        />
        <span
          className="text-sm"
          style={{
            fontFamily: fonts.body,
            fontWeight: 700,
            color: colors.navy,
          }}
        >
          150K Fam
        </span>
      </motion.div>

      <motion.div
        className="absolute z-20 rounded-2xl shadow-lg px-4 py-3 flex items-center gap-2 hidden lg:flex"
        style={{ backgroundColor: colors.cream, top: "30%", right: "8%" }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: [0, 6, 0] }}
        transition={{
          opacity: { duration: 0.6, delay: 1.4 },
          y: { repeat: Infinity, duration: 3.5, ease: "easeInOut", delay: 1.4 },
        }}
      >
        <Scissors size={18} style={{ color: theme.accent }} />
        <span
          className="text-sm"
          style={{
            fontFamily: fonts.body,
            fontWeight: 700,
            color: colors.navy,
          }}
        >
          Creating Daily
        </span>
      </motion.div>

      <motion.div
        className="absolute z-20 rounded-2xl shadow-lg px-4 py-3 flex items-center gap-2 hidden lg:flex"
        style={{ backgroundColor: colors.cream, bottom: "26%", left: "16%" }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: [0, -5, 0] }}
        transition={{
          opacity: { duration: 0.6, delay: 1.6 },
          y: { repeat: Infinity, duration: 4, ease: "easeInOut", delay: 1.6 },
        }}
      >
        <span style={{ color: theme.warm, fontSize: 18 }}>✨</span>
        <span
          className="text-sm"
          style={{
            fontFamily: fonts.body,
            fontWeight: 700,
            color: colors.navy,
          }}
        >
          Retreats 2026
        </span>
      </motion.div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 w-full text-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.p
            variants={fadeUp}
            custom={0}
            className="text-xs uppercase mb-4"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              letterSpacing: "0.25em",
              color: theme.accent,
            }}
          >
            A Few Loose Threads
          </motion.p>

          <motion.h1
            variants={fadeUp}
            custom={1}
            className="text-5xl sm:text-7xl lg:text-8xl leading-[1.05] mb-8"
            style={{
              fontFamily: fonts.logo,
              fontWeight: 400,
              color: colors.navy,
            }}
          >
            Loose threads,{" "}
            <span
              className="relative inline-block"
              style={{ color: theme.accent }}
            >
              big ideas
              <motion.svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 200 12"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.2, delay: 0.8 }}
              >
                <motion.path
                  d="M2 8 Q50 2, 100 7 T198 5"
                  stroke={theme.warm}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray="6 4"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.2, delay: 0.8 }}
                />
              </motion.svg>
            </span>
            <span style={{ color: theme.warm }}>!</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="text-base sm:text-lg leading-relaxed mb-10 max-w-xl mx-auto"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: colors.navy,
              opacity: 0.75,
              letterSpacing: "0.03em",
            }}
          >
            Step away from the noise and into a weekend of sewing, creativity,
            and connection at my next retreat.
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={3}
            className="flex flex-col sm:flex-row items-center gap-4 justify-center"
          >
            <Link
              to="/retreats"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white cursor-pointer transition-all duration-250 hover:shadow-xl hover:scale-105 active:scale-[0.98]"
              style={{
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase" as const,
                fontSize: "0.75rem",
                backgroundColor: theme.accent,
              }}
            >
              Explore Retreats
              <ArrowRight size={16} />
            </Link>
            <button
              type="button"
              className="inline-flex items-center gap-2 px-4 py-3.5 cursor-pointer transition-opacity duration-250 hover:opacity-100"
              style={{
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase" as const,
                fontSize: "0.75rem",
                color: colors.navy,
                opacity: 0.7,
              }}
            >
              <Play size={14} fill={colors.navy} />
              Watch the tour
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
